// ─── Mixed Method Tension Engine ──────────────────────────────────────────────
// Generates realistic qualitative–quantitative tensions.
// Real doctoral research almost always contains mismatch between what the
// numbers show and what the people say. This engine produces that mismatch
// systematically rather than letting the AI invent harmonious alignment.

import { getDomainProfile } from './domainRealismProfiles'

// ── Tension templates ─────────────────────────────────────────────────────────

const TENSION_TEMPLATES = {
  // Pattern: quantitative shows positive effect, qualitative contradicts implementation
  quantPositiveQualNegative: (iv, dv, magnitude) => ({
    type: 'IMPLEMENTATION_GAP',
    label: 'Policy-Implementation Gap',
    quantSummary: `${iv} shows a statistically ${magnitude} positive association with ${dv} (p < .05)`,
    qualSummary: `Interview respondents describe ${iv} as formally present but operationally ineffective`,
    thematicQuotes: [
      `"We follow the procedures on paper, but the practical reality on the ground is completely different."`,
      `"The metrics look good in the reports, but we know that [${iv}] doesn't actually change what happens day to day."`,
      `"There is a significant gap between what is mandated and what is actually implemented."`,
    ],
    tensionNarrative: `This tension between the statistically positive coefficient for ${iv} and the qualitative accounts of operational dysfunction represents a classic implementation gap. The quantitative association may capture formal adoption of ${iv}-related policies, while field-level implementation remains fragmented. This decoupling is well-documented in institutional theory as isomorphic mimicry — organisations adopt the form of change without its function.`,
  }),

  // Pattern: quantitative non-significant, qualitative shows importance
  quantNonSigQualImportant: (iv, dv) => ({
    type: 'LATENT_SALIENCE',
    label: 'Latent Salience',
    quantSummary: `${iv} does not reach statistical significance in the regression model (p > .05)`,
    qualSummary: `Respondents consistently identify ${iv} as critically important to ${dv}`,
    thematicQuotes: [
      `"This is actually the most important factor — but it's very hard to measure formally."`,
      `"On paper it doesn't show up, but everyone who works here knows it matters."`,
      `"The numbers can't capture this relationship because it works through informal channels."`,
    ],
    tensionNarrative: `The absence of statistical significance for ${iv} in the quantitative model should not be interpreted as evidence of irrelevance. Qualitative data reveals that respondents consistently foreground this factor as consequential. The divergence may reflect measurement limitations — the construct captured by the quantitative indicator may not adequately represent the nuanced phenomenon that respondents describe. This is consistent with Creswell and Plano Clark's (2018) observation that quantitative and qualitative data often capture distinct facets of the same social phenomenon.`,
  }),

  // Pattern: quantitative shows negative, qualitative shows positive narrative
  quantNegativeQualPositive: (iv, dv) => ({
    type: 'PERCEPTUAL_OPTIMISM',
    label: 'Perceptual Optimism Bias',
    quantSummary: `${iv} shows a negative or near-zero relationship with ${dv} in regression analysis`,
    qualSummary: `Stakeholders express positive perceptions of ${iv}'s contribution to ${dv}`,
    thematicQuotes: [
      `"We feel confident that our ${iv} initiatives are making a real difference."`,
      `"The changes we've made have definitely improved things — we can see it in team morale."`,
      `"I genuinely believe we are doing better on this dimension, even if it's hard to prove."`,
    ],
    tensionNarrative: `A noteworthy divergence emerges between the quantitative evidence and stakeholder perceptions. While regression results do not confirm a positive contribution of ${iv} to ${dv}, interview data reveals widespread optimism among practitioners. This perceptual-empirical mismatch is consistent with social desirability bias in self-report data and may also reflect the tendency of institutional actors to perceive improvements that align with their professional identity and organisational mission.`,
  }),

  // Pattern: corruption/informal sector contradicts formal metrics
  informalSectorContradiction: (domain) => ({
    type: 'INFORMAL_DYNAMICS',
    label: 'Formal-Informal System Contradiction',
    quantSummary: 'Formal institutional indicators show positive trends in the quantitative analysis',
    qualSummary: 'Qualitative accounts reveal significant informal sector activity and unofficial practices',
    thematicQuotes: [
      `"The formal system works well on paper. What you don't see is the parallel system that actually gets things done."`,
      `"There are two realities here — the official one, and the actual one."`,
      `"People find workarounds. The statistics don't capture the workarounds."`,
    ],
    tensionNarrative: `A fundamental tension in the data concerns the relationship between formal institutional performance and informal operational reality. While quantitative indicators suggest institutional improvement, qualitative fieldwork reveals the persistence of informal networks, unofficial processes, and workaround behaviours that the formal metrics cannot capture. This finding aligns with Khan's (2010) concept of "working with the grain" — institutional change in developing contexts often occurs through informal channels that bypass formal structures while nominally complying with them.`,
  }),

  // Pattern: sector/regional heterogeneity
  heterogeneityTension: (iv) => ({
    type: 'HETEROGENEOUS_EFFECTS',
    label: 'Context-Specific Effect Heterogeneity',
    quantSummary: `${iv} shows a pooled average effect across all units in the sample`,
    qualSummary: 'Qualitative data reveals substantial between-group variation that the pooled estimate obscures',
    thematicQuotes: [
      `"What works in the urban context simply doesn't translate to rural municipalities."`,
      `"The larger units can absorb the changes. For us, the same policy has completely different implications."`,
      `"You can't average out something this context-dependent."`,
    ],
    tensionNarrative: `The pooled regression coefficient for ${iv} conceals substantial heterogeneity that qualitative data makes legible. Respondents from different contextual positions describe markedly different experiences of the variable's influence, suggesting that the average effect obscures important subgroup variation. This has methodological implications: future research should consider multilevel modelling or sample stratification to examine whether the ${iv}–${dv} relationship varies systematically by context.`,
  }),
}

// ── Tension generator ─────────────────────────────────────────────────────────

function chooseTensionType(coef, domain) {
  if (!coef) return 'heterogeneityTension'

  if (coef.significant && coef.beta > 0) {
    // Positive significant — most likely implementation gap in policy domains
    if (['public_policy', 'sustainability', 'education', 'healthcare'].includes(domain)) {
      return 'quantPositiveQualNegative'
    }
    return Math.random() < 0.4 ? 'heterogeneityTension' : 'quantPositiveQualNegative'
  }

  if (!coef.significant) {
    return Math.random() < 0.5 ? 'quantNonSigQualImportant' : 'heterogeneityTension'
  }

  if (coef.beta < 0) {
    return Math.random() < 0.4 ? 'quantNegativeQualPositive' : 'heterogeneityTension'
  }

  return 'heterogeneityTension'
}

function magnitudeLabel(beta) {
  const abs = Math.abs(beta)
  if (abs > 0.5) return 'strong'
  if (abs > 0.25) return 'moderate'
  return 'weak'
}

/**
 * Generate a qualitative–quantitative tension narrative from computed OLS results.
 *
 * @param {object} olsResult - from statisticsEngine.computeOLS
 * @param {string} domain    - research domain
 * @param {object} thesis    - thesis metadata
 * @returns {QualQuantTension[]}
 */
export function generateMixedMethodTensions(olsResult, domain, thesis) {
  if (!olsResult) return []

  const profile = getDomainProfile(domain)
  const tensions = []
  const coefs = Object.entries(olsResult.coefficients).filter(([k]) => k !== 'const')
  const dvName = olsResult.dv

  // Generate a tension for each IV (limit to 3 most interesting)
  const interesting = coefs
    .sort((a, b) => {
      // Prioritise: non-sig predictors, reversed predictors, weak positives
      const scoreA = a[1].significant ? 0 : 2 + (a[1].beta < 0 ? 1 : 0)
      const scoreB = b[1].significant ? 0 : 2 + (b[1].beta < 0 ? 1 : 0)
      return scoreB - scoreA
    })
    .slice(0, Math.min(3, coefs.length))

  for (const [ivName, coef] of interesting) {
    const tensionType = chooseTensionType(coef, domain)
    let tension

    if (tensionType === 'quantPositiveQualNegative') {
      tension = TENSION_TEMPLATES.quantPositiveQualNegative(ivName, dvName, magnitudeLabel(coef.beta))
    } else if (tensionType === 'quantNonSigQualImportant') {
      tension = TENSION_TEMPLATES.quantNonSigQualImportant(ivName, dvName)
    } else if (tensionType === 'quantNegativeQualPositive') {
      tension = TENSION_TEMPLATES.quantNegativeQualPositive(ivName, dvName)
    } else {
      tension = TENSION_TEMPLATES.heterogeneityTension(ivName)
    }

    tensions.push({
      ...tension,
      iv: ivName,
      dv: dvName,
      coef: { beta: coef.beta, p: coef.p_value, sig: coef.significant },
    })
  }

  // Add domain-specific informal sector tension if relevant
  if (profile.contradictionLikelihood > 0.55 && tensions.length < 3) {
    tensions.push({
      ...TENSION_TEMPLATES.informalSectorContradiction(domain),
      iv: 'institutional_context',
      dv: dvName,
      coef: null,
    })
  }

  return tensions
}

/**
 * Generate thematic interview summaries (not real quotes — synthetic themes).
 * These represent what a qualitative strand might plausibly find.
 */
export function generateQualitativeThemes(domain, olsResult) {
  const profile = getDomainProfile(domain)
  const baseThemes = profile.qualitativeThemes || []
  const domainNarratives = profile.dominantNarratives || []

  // Pull in non-significant predictors for latent-salience theme
  const nonSigIVs = olsResult
    ? Object.entries(olsResult.coefficients)
        .filter(([k, c]) => k !== 'const' && !c.significant)
        .map(([k]) => k)
    : []

  const themes = [
    ...baseThemes.slice(0, 3),
    ...nonSigIVs.slice(0, 1).map(iv => (
      `Respondents consistently described ${iv} as operationally important, even though it does not emerge as statistically significant in the quantitative model — suggesting a measurement-reality gap for this construct.`
    )),
  ]

  return {
    themes,
    dominantNarratives: domainNarratives,
    methodologicalNote: `Thematic analysis of semi-structured interviews (n = 12–18, purposive sample) revealed four primary themes that both corroborate and complicate the quantitative findings. The qualitative strand provides explanatory depth that the regression model cannot supply — particularly regarding the mechanisms and contextual conditionalities that shape the relationships observed.`,
  }
}
